import React, { createContext, ReactNode, useEffect, useMemo } from 'react';
import * as amplitude from '@amplitude/analytics-browser';
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';
import { useUserStore } from '@client/store';
import { CharacterPayload, CharacterPayloadSchema } from '@client/components';

const AmplitudeContext = createContext<any>(null);

interface AmplitudeProviderProps {
  children: ReactNode;
  apiKey?: string;
  options?: {
    autocapture?: boolean;
    sampleRate?: number;
  };
}

export function AmplitudeProvider({
  children,
  apiKey = '408a394190372749f2a9e6157f91f981',
  options = { autocapture: true, sampleRate: 1 },
}: AmplitudeProviderProps) {
  const userUUID = useUserStore((state) => state.userUUID);

  useEffect(() => {
    if (!amplitude.getDeviceId()) {
      try {
        amplitude.add(sessionReplayPlugin({ sampleRate: options.sampleRate }));
        amplitude.init(apiKey, { autocapture: options.autocapture });
        if (userUUID) amplitude.setUserId(userUUID);
      } catch (err: any) {
        if (err.name !== 'AbortError') console.error('Amplitude init failed:', err);
      }
    }
  }, [apiKey, options.autocapture, options.sampleRate, userUUID]);

  const validatePayload = (character: CharacterPayload) => {
    const parsed = CharacterPayloadSchema.safeParse(character);
    if (!parsed.success) {
      console.warn('Invalid CharacterPayload', parsed.error);
      return character;
    }
    return parsed.data;
  };

  const trackCharacterCreated = (character: CharacterPayload, result?: any) =>
    amplitude.track('Character Created', {
      ...validatePayload(character),
      character_uuid: result?.char_uuid ?? character.char_uuid,
    });

  const trackCharacterUpdated = (character: CharacterPayload, result?: any) =>
    amplitude.track('Character Updated', {
      ...validatePayload(character),
      character_uuid: result?.char_uuid ?? character.char_uuid,
    });

  const trackCharacterDrafted = (character: CharacterPayload, creatorUuid?: string) =>
    amplitude.track('Character Drafted', {
      ...validatePayload(character),
      creator_uuid: creatorUuid ?? character.creator,
    });

  const trackCharacterCreationFailed = (character: CharacterPayload, error: string) =>
    amplitude.track('Character Creation Failed', {
      ...validatePayload(character),
      error_message: error,
    });

  const trackCharacterUpdateFailed = (character: CharacterPayload, error: string) =>
    amplitude.track('Character Update Failed', {
      ...validatePayload(character),
      error_message: error,
    });

  const trackCharacterDraftFailed = (character: CharacterPayload, error: string) =>
    amplitude.track('Character Draft Failed', {
      ...validatePayload(character),
      error_message: error,
    });

  const contextValue = useMemo(
    () => ({
      amplitude,
      trackCharacterCreated,
      trackCharacterUpdated,
      trackCharacterDrafted,
      trackCharacterCreationFailed,
      trackCharacterUpdateFailed,
      trackCharacterDraftFailed,
    }),
    []
  );

  return <AmplitudeContext.Provider value={contextValue}>{children}</AmplitudeContext.Provider>;
}

export { AmplitudeContext };
